import React, { useState } from 'react';
import EarningsTile from './EarningsTile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export const formatGroupDate = (dateStr) => {
  if (!dateStr) return 'Unknown Date';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));

  if (diff === -1) return 'Yesterday';
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return target.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const getDayBadgeStyle = (dateStr) => {
  const base = {
    display: 'inline-flex', alignItems: 'center',
    padding: '4px 10px', borderRadius: 8,
    fontSize: 12, fontWeight: 700,
  };
  if (!dateStr) return { ...base, background: '#f3f4f6', color: '#6b7280' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { ...base, background: '#d1d5db', color: '#374151' };
  if (diff === 0) return { ...base, background: '#4f46e5', color: '#fff' };
  if (diff === 1) return { ...base, background: '#e0e7ff', color: '#4338ca' };
  return { ...base, background: '#f3f4f6', color: '#6b7280' };
};

// Shared tile list — used by the date groups here and by EarningsDayPanel.
export const EarningsTileList = ({ stocks, loggedInUser, expandedTicker, onToggleTicker, onLoginSuccess, onOpenLabelManager, newsMap, highlightsMap }) => (
  <div className="px-4 space-y-2">
    {stocks.map((stock, i) => (
      <EarningsTile
        key={stock.symbol}
        stock={stock}
        loggedInUser={loggedInUser}
        isExpanded={expandedTicker === stock.symbol}
        onToggle={() => onToggleTicker(stock.symbol)}
        onLoginSuccess={onLoginSuccess}
        onOpenLabelManager={onOpenLabelManager}
        news={newsMap?.[stock.symbol]}
        callHighlights={highlightsMap?.[stock.symbol]}
        index={i}
      />
    ))}
  </div>
);

const EarningsDateGroup = ({ date, stocks, loggedInUser, expandedTicker, onToggleTicker, onLoginSuccess, onOpenLabelManager, defaultCollapsed = false, newsMap, highlightsMap }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const badgeStyle = getDayBadgeStyle(date);

  return (
    // scrollMarginTop keeps the sticky page header from covering the group
    // when histogram bar clicks scrollIntoView() this element.
    <div id={`earnings-group-${date}`} style={{ marginBottom: collapsed ? 4 : 32, scrollMarginTop: 130 }}>
      {/* Date header - clickable to toggle */}
      <div
        style={{
          position: 'sticky', top: 105, zIndex: 5,
          padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', userSelect: 'none',
          background: '#f8f9fb',
        }}
        onClick={() => setCollapsed(c => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(c => !c); } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18, color: '#6b7280',
              transition: 'transform 0.2s',
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            }}
          />
          <span style={badgeStyle}>
            {formatGroupDate(date)}
          </span>
          <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
            {stocks.length} stock{stocks.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, #e5e7eb, transparent)' }} />
      </div>

      {/* Tiles - collapsible */}
      {!collapsed && (
        <EarningsTileList
          stocks={stocks}
          loggedInUser={loggedInUser}
          expandedTicker={expandedTicker}
          onToggleTicker={onToggleTicker}
          onLoginSuccess={onLoginSuccess}
          onOpenLabelManager={onOpenLabelManager}
          newsMap={newsMap}
          highlightsMap={highlightsMap}
        />
      )}
    </div>
  );
};

export default React.memo(EarningsDateGroup);
